/**
 * Todos os dados da API em um só lugar (React Query).
 * - `useX()` busca e mantém em cache.
 * - `useAction()` executa uma alteração, mostra o aviso e atualiza as listas afetadas.
 */
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useToast } from '@dotapps/ui';
import type {
  Budget,
  Client,
  Company,
  Contract,
  ContractModels,
  Deal,
  FeatureFlags,
  LibraryItem,
  Meeting,
  Project,
  ProposalLink,
  ProposalOptions,
  Stage,
  Supplier,
  TenantSettings,
  User,
  BudgetItem,
  Receivable,
} from '@dotapps/core';
import { api } from './api';

/* ---------------- chaves de cache ---------------- */

export const keys = {
  me: ['me'],
  users: ['users'],
  companies: ['companies'],
  settings: ['settings'],
  clients: ['clients'],
  clientTimeline: (id: string) => ['clients', id, 'timeline'],
  suppliers: ['suppliers'],
  stages: ['stages'],
  deals: ['deals'],
  meetings: ['meetings'],
  budgets: ['budgets'],
  budgetLinks: (id: string) => ['budgets', id, 'links'],
  contracts: ['contracts'],
  projects: ['projects'],
  library: ['library'],
  receivables: ['receivables'],
} as const;

/* ---------------- respostas especiais ---------------- */

export interface SettingsResponse {
  settings: TenantSettings;
  features: FeatureFlags;
  contractModels: ContractModels;
  /** "hoje" no fuso do escritório */
  today: string;
  integrations: { google: boolean; calendar: boolean; ai: string };
}

export interface TimelineEntry {
  date: string;
  title: string;
  text: string;
  by: string | null;
  kind: string;
}

export type LinkWithUrl = ProposalLink & { url: string };

export interface TaskSaveResult {
  project: Project;
  domino: { task: string; days: number; affected: string[]; endBefore: string; endAfter: string } | null;
  freed: string[];
}

export interface AiResult<T> {
  live: boolean;
  data: T;
}

export interface AtaResult {
  resumo: string;
  decisoes: string[];
  pendencias: Array<{ oque: string; quem: string }>;
  proximos: string[];
}

export interface AiBudget {
  ambientes: Array<{ nome: string; itens: Array<Omit<BudgetItem, 'id'>> }>;
  observacoes: string;
}

export interface CnpjLookup {
  nome: string;
  fantasia: string;
  socio: string;
  cidade: string;
  uf: string;
  cep: string;
  endereco: string;
  tel: string;
  email: string;
  situacao: string;
}

/* ---------------- leituras ---------------- */

const STALE = 30_000;

export const useMe = (enabled = true) =>
  useQuery({ queryKey: keys.me, queryFn: () => api.get<User>('/auth/me'), enabled, staleTime: STALE });
export const useUsers = () =>
  useQuery({ queryKey: keys.users, queryFn: () => api.get<User[]>('/users'), staleTime: STALE });
export const useCompanies = () =>
  useQuery({ queryKey: keys.companies, queryFn: () => api.get<Company[]>('/companies'), staleTime: STALE });
export const useSettings = () =>
  useQuery({ queryKey: keys.settings, queryFn: () => api.get<SettingsResponse>('/settings'), staleTime: STALE });
/** `carteira`: filtro do administrador (só nas telas Negociação e Cadastros); sem ele, o escritório inteiro. */
export const useClients = (carteira = 'all') =>
  useQuery({
    queryKey: [...keys.clients, carteira],
    queryFn: () => api.get<Client[]>('/clients', carteira),
    staleTime: STALE,
  });
export const useClientTimeline = (id: string) =>
  useQuery({ queryKey: keys.clientTimeline(id), queryFn: () => api.get<TimelineEntry[]>(`/clients/${id}/timeline`) });
export const useSuppliers = () =>
  useQuery({ queryKey: keys.suppliers, queryFn: () => api.get<Supplier[]>('/suppliers'), staleTime: STALE });
export const useStages = () =>
  useQuery({ queryKey: keys.stages, queryFn: () => api.get<Stage[]>('/stages'), staleTime: STALE });
/** Receitas (só administradores; para os outros perfis a busca fica desligada). */
export const useReceivables = (enabled = true) =>
  useQuery({
    queryKey: keys.receivables,
    queryFn: () => api.get<Receivable[]>('/receivables'),
    staleTime: STALE,
    enabled,
  });
export const useDeals = (carteira = 'all') =>
  useQuery({
    queryKey: [...keys.deals, carteira],
    queryFn: () => api.get<Deal[]>('/deals', carteira),
    staleTime: STALE,
  });
export const useMeetings = () =>
  useQuery({ queryKey: keys.meetings, queryFn: () => api.get<Meeting[]>('/meetings'), staleTime: STALE });
export const useBudgets = () =>
  useQuery({ queryKey: keys.budgets, queryFn: () => api.get<Budget[]>('/budgets'), staleTime: STALE });
export const useBudgetLinks = (id: string | undefined) =>
  useQuery({
    queryKey: keys.budgetLinks(id ?? ''),
    queryFn: () => api.get<LinkWithUrl[]>(`/budgets/${id}/links`),
    enabled: !!id,
  });
export const useContracts = () =>
  useQuery({ queryKey: keys.contracts, queryFn: () => api.get<Contract[]>('/contracts'), staleTime: STALE });
export const useProjects = () =>
  useQuery({ queryKey: keys.projects, queryFn: () => api.get<Project[]>('/projects'), staleTime: STALE });
export const useLibrary = () =>
  useQuery({ queryKey: keys.library, queryFn: () => api.get<LibraryItem[]>('/library'), staleTime: STALE });

/* ---------------- alterações ---------------- */

interface ActionOptions<R> {
  /** listas que mudam com esta ação */
  invalidate?: QueryKey[];
  /** aviso de sucesso (texto fixo ou montado a partir da resposta) */
  success?: string | ((r: R) => string);
  /** atualiza o cache direto com a resposta (sem buscar de novo) */
  onDone?: (r: R) => void;
}

/**
 * Executa uma alteração na API com aviso de sucesso/erro e atualização das listas.
 *   const save = useAction((c: ClientInput) => api.post('/clients', c), { invalidate: [keys.clients], success: 'Cliente salvo' });
 *   await save(dados);   // lança erro se falhar (o aviso de erro já é mostrado)
 */
export function useAction<A, R>(fn: (args: A) => Promise<R>, opts: ActionOptions<R> = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: fn,
    onSuccess: async r => {
      opts.onDone?.(r);
      await Promise.all((opts.invalidate ?? []).map(k => qc.invalidateQueries({ queryKey: k })));
      const msg = typeof opts.success === 'function' ? opts.success(r) : opts.success;
      if (msg) toast(msg);
    },
    onError: (err: Error) => toast(err.message, 'warn'),
  });
  const run = (args: A) => m.mutateAsync(args);
  return Object.assign(run, { pending: m.isPending });
}

/* ---------------- chamadas usadas em várias telas ---------------- */

export const calls = {
  createLink: (budgetId: string, options: ProposalOptions) =>
    api.post<LinkWithUrl>(`/budgets/${budgetId}/links`, options),
  lookupCnpj: (cnpj: string) => api.get<CnpjLookup>(`/lookup/cnpj/${cnpj.replace(/\D/g, '')}`),
  lookupCep: (cep: string) =>
    api.get<{ cidade: string; uf: string; rua: string }>(`/lookup/cep/${cep.replace(/\D/g, '')}`),
  aiChat: (messages: Array<{ role: 'user' | 'assistant'; content: string }>) =>
    api.post<AiResult<string>>('/ai/chat', { messages }),
  aiAta: (dealId: string | null, transcript: string) =>
    api.post<AiResult<AtaResult>>('/ai/ata', { dealId, transcript }),
  aiBudget: (description: string) => api.post<AiResult<AiBudget>>('/ai/budget', { description }),
  aiProjectMessage: (projectId: string, showCost: boolean) =>
    api.post<AiResult<{ text: string; phone: string; contato: string }>>('/ai/project-message', {
      projectId,
      showCost,
    }),
};
